import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import {
	AI_SECTION,
	AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR,
	AI_SECTION_COMMENT_PRIMARY_TOOLBAR,
	AI_SECTION_PRIMARY_TOOLBAR,
	AI_SUGGESTIONS_GROUP,
	AI_SUGGESTIONS_BUTTON,
	AI_SECTION_RANK,
	AI_SUGGESTIONS_GROUP_RANK,
	ROVO_GROUP_DISPLAY_SMALL,
	ROVO_GROUP_DISPLAY_SMALL_RANK,
} from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Show, ToolbarButton, ToolbarButtonGroup } from '@atlaskit/editor-toolbar';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import {
	createEntryPointExposureSession,
	type EntryPointExposureSession,
	useEntryPointExposureAnalytics,
} from './use-entry-point-exposure-analytics';

const AiSuggestionsButton = ({
	api,
	exposureSession,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	exposureSession: EntryPointExposureSession;
	onShowSidebar?: () => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const label = formatMessage(aiSuggestionsMessages.suggestEditsButtonLabel);
	const exposureRef = useEntryPointExposureAnalytics({
		api,
		entryPoint: 'primaryToolbar',
		exposureSession,
	});
	const isThinking = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => state.aiSuggestionsState?.thinkingStatus === 'thinking',
	);

	const handleClick = useCallback(() => {
		if (isThinking) {
			return;
		}
		api?.aiSuggestions?.actions.setThinkingState('thinking', { entryPoint: 'primaryToolbar' });
	}, [api, isThinking]);

	return (
		<Tooltip content={formatMessage(aiSuggestionsMessages.suggestEditsButtonTooltip)}>
			<ToolbarButton
				ref={exposureRef}
				onClick={handleClick}
				testId="editor-toolbar__ai-suggestions-button"
				iconBefore={<TextReviewIcon label={label} size="small" />}
				ignoreInteractionDisabled
				isDisabled={isThinking}
			>
				{label}
			</ToolbarButton>
		</Tooltip>
	);
};

export const AISuggestionsToolbarComponents = ({
	api,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
}): RegisterComponent[] => {
	// Scope exposure tracking to this toolbar registration, not the whole browser session.
	const exposureSession = createEntryPointExposureSession();

	return [
		// AI SUGGESTIONS GROUP — only registered in the pinned primary toolbar sections,
		// not in AI_SECTION (which is used by the floating toolbar).
		//
		// The group wraps its children in <Show above="lg"> so it hides below the breakpoint
		{
			type: AI_SUGGESTIONS_GROUP.type,
			key: AI_SUGGESTIONS_GROUP.key,
			parents: [
				{
					type: AI_SECTION.type,
					key: AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
				{
					type: AI_SECTION.type,
					key: AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
				{
					type: AI_SECTION_PRIMARY_TOOLBAR.type,
					key: AI_SECTION_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
			],
			component: ({ children }: { children: React.ReactNode }) => (
				<Show above="lg">
					<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
				</Show>
			),
		},
		// AI SUGGESTIONS BUTTON — shown as a full labelled button when space is available
		// (via AI_SUGGESTIONS_GROUP above "lg"), or as an icon-only button with tooltip
		// inside ROVO_GROUP_DISPLAY_SMALL when space is tight (below "lg").
		{
			type: AI_SUGGESTIONS_BUTTON.type,
			key: AI_SUGGESTIONS_BUTTON.key,
			parents: [
				{
					type: AI_SUGGESTIONS_GROUP.type,
					key: AI_SUGGESTIONS_GROUP.key,
					rank: AI_SUGGESTIONS_GROUP_RANK[AI_SUGGESTIONS_BUTTON.key],
				},
				{
					type: ROVO_GROUP_DISPLAY_SMALL.type,
					key: ROVO_GROUP_DISPLAY_SMALL.key,
					rank: ROVO_GROUP_DISPLAY_SMALL_RANK[AI_SUGGESTIONS_BUTTON.key],
				},
			],
			component: () => <AiSuggestionsButton api={api} exposureSession={exposureSession} />,
		},
	];
};
